import {inject, Injectable} from '@angular/core';
import {Database, listVal, objectVal, ref} from '@angular/fire/database';
import {combineLatest, map, Observable, shareReplay, startWith, switchMap, take, timer} from 'rxjs';
import {AgendaItem, Award, Awards, EventDetails, FlatGameSession, FlatJudgingSession, GameSession, JudgingSession, Leaderboard, LocationAssignments, RegionSummaries, ScheduleConfig, Team, TeamDetails} from './models';
import {Rubric, RUBRIC_SCALE, TeamAwards, TeamRubric} from './rubric.models';
import {Scoresheet, ScoresheetStatuses, TeamScoresheets} from './scoresheet.models';
import {generateHash} from './utils';

@Injectable({
  providedIn: 'root'
})
export class EventDataService {
  private readonly db = inject(Database);

  // Shared timer to synchronize regular updates.
  private readonly oneSecondTimer$ = timer(0, 1000);

  private getServerOffset() {
    return objectVal<number>(ref(this.db, `.info/serverTimeOffset`)).pipe(shareReplay(1));
  }

  now() {
    return this.getServerOffset().pipe(take(1), map(offset => Date.now() + offset));
  }

  nowTimer(interval = 1000) {
    const timer$ = interval === 1000 ? this.oneSecondTimer$.pipe(startWith(0)) : timer(0, interval);
    return combineLatest([this.getServerOffset(), timer$]).pipe(map(([offset]) => Date.now() + offset));
  }

  getRegionName(regionKey: string) {
    return objectVal<string>(ref(this.db, `/regionSummaries/${regionKey}/name`)).pipe(map(name => name ? name : ''));
  }

  getEventName(regionKey: string, eventKey: string) {
    return objectVal<string>(ref(this.db, `/regionSummaries/${regionKey}/events/${eventKey}/name`)).pipe(map(name => name ? name : ''));
  }

  getEventOrRegionName(regionKey: string, eventKey?: string) {
    return eventKey ? this.getEventName(regionKey, eventKey) : this.getRegionName(regionKey);
  }
  
  getEventDetails(regionKey: string, eventKey: string) {
    return objectVal<EventDetails>(ref(this.db, `/regionSummaries/${regionKey}/events/${eventKey}`));
  }
  
  getRegionSummaries() {
    return objectVal<RegionSummaries>(ref(this.db, `/regionSummaries`));
  }

  getTeamName(regionKey: string, eventKey: string, teamNumber: string): Observable<string|undefined> {
    return objectVal<string|undefined>(ref(this.db, `regions/${regionKey}/events/${eventKey}/teams/${teamNumber}/name`));
  }

  getSetting(regionKey: string, eventKey: string, settingKey: string): Observable<any> {
    return objectVal<any>(ref(this.db, `regions/${regionKey}/events/${eventKey}/settings/${settingKey}`));
  }

  getTeams(regionKey: string, eventKey: string): Observable<Team[]> {
    return objectVal<{[teamNumber: string]: TeamDetails}>(ref(this.db, `regions/${regionKey}/events/${eventKey}/teams`)).pipe(
      map(details => {
        const results: Team[] = [];
        for(const [teamNumber, td] of Object.entries(details || {})) {
          results.push({
            ...td,
            teamNumber,
          });
        }

        results.sort((a,b) => a.teamNumber.localeCompare(b.teamNumber, undefined, {numeric: true, sensitivity: 'base'}));

        return results;
      }),
    );
  }

  getAllTeamDetails(regionKey: string, eventKey: string): Observable<{[teamNumber: string]: TeamDetails}> {
    return objectVal<{[teamNumber: string]: TeamDetails}>(ref(this.db, `regions/${regionKey}/events/${eventKey}/teams`));
  }

  getTeam(regionKey: string, eventKey: string, teamNumber: string): Observable<Team> {
    return objectVal<Team>(ref(this.db, `regions/${regionKey}/events/${eventKey}/teams/${teamNumber}`)).pipe(
      map(details => {
        if(details) {
          details.teamNumber = teamNumber;
        }
        return details;
      }),
    );
  }

  getJudgingSessions(regionKey: string, eventKey: string): Observable<JudgingSession[]> {
    return combineLatest([objectVal<{[sessionKey: string]: JudgingSession}>(ref(this.db, `regions/${regionKey}/events/${eventKey}/judging/sessions`)), this.getScheduleConfig(regionKey, eventKey, 'judging')]).pipe(
      map(([obj, config]) => {
        const results: JudgingSession[] = [];
        let dynamicTime: number|undefined = undefined;
        for(const [key, session] of Object.entries(obj || {})) {
          if(dynamicTime) {
            dynamicTime += config.duration + config.resetDuration;
          }

          if(session.time) {
            dynamicTime = session.time;
          }

          results.push({
            ...session,
            key,
            dynamicTime: dynamicTime || undefined,
            type: 'judging',
          });
        }

        results.sort((a,b) => (a.dynamicTime || 0) - (b.dynamicTime || 0));
        return results;
      }),
    );
  }

  getFlatJudgingSessions(regionKey: string, eventKey: string): Observable<FlatJudgingSession[]> {
    return combineLatest([this.getJudgingSessions(regionKey, eventKey), this.getScheduleConfig(regionKey, eventKey, 'judging')]).pipe(map(([grouped, config]) => {
      const results: FlatJudgingSession[] = [];
      for(const s of grouped) {
        for(const [location, teamNumber] of Object.entries(s.teams || {})) {
          if(teamNumber) {
            results.push({
              type: 'judging',
              key: s.key,
              dynamicTime: s.dynamicTime,
              location: +location,
              locationName: config.locationNames?.[location],
              teamNumber,
            })
          }
        }
      }

      return results;
    }));
  }

  getGameSessions(regionKey: string, eventKey: string): Observable<GameSession[]> {
    return combineLatest([objectVal<{[sessionKey: string]: GameSession}>(ref(this.db, `regions/${regionKey}/events/${eventKey}/games/sessions`)), this.getScheduleConfig(regionKey, eventKey, 'games')]).pipe(
      map(([obj, config]) => {
        const results: GameSession[] = [];
        const dynamicCycle = (config.duration + config.resetDuration) / (config.maxConcurrentCount ? Math.ceil(config.count / config.maxConcurrentCount): 1);
        let dynamicTime: number|undefined = undefined;

        for(const [key, session] of Object.entries(obj || {})) {
          if(dynamicTime) {
            dynamicTime += dynamicCycle;
          }

          if(session.time) {
            dynamicTime = session.time;
          }

          results.push({
            ...session,
            key,
            dynamicTime,
            type: 'game',
          });
        }

        return results;
      }),
    );
  }

  getFlatGameSessions(regionKey: string, eventKey: string): Observable<FlatGameSession[]> {
    return combineLatest([this.getGameSessions(regionKey, eventKey), this.getScheduleConfig(regionKey, eventKey, 'games')]).pipe(map(([grouped, config]) => {
      const results: FlatGameSession[] = [];
      const roundByTeam: {[teamNumber: string]: number} = {};
      const practiceRoundByTeam: {[teamNumber: string]: number} = {};

      for(const s of grouped) {
        for(const [location, teamNumber] of Object.entries(s.teams || {})) {
          if(teamNumber) {
            let map = roundByTeam;
            if(s.practice) {
              map = practiceRoundByTeam;
            }
            if(!map[teamNumber]) {
              map[teamNumber] = 0;
            }
            map[teamNumber]++;

            results.push({
              type: 'game',
              key: s.key,
              dynamicTime: s.dynamicTime,
              location: +location,
              locationName: config.locationNames?.[location],
              teamNumber,
              practice: s.practice,
              matchType: s.practice ? 'practice' : 'rounds',
              round: map[teamNumber],
            })
          }
        }
      }

      return results;
    }));
  }

  getAgenda(regionKey: string, eventKey: string, allowInvalid?: boolean): Observable<AgendaItem[]> {
    return objectVal<{[itemKey: string]: AgendaItem}>(ref(this.db, `regions/${regionKey}/events/${eventKey}/agenda`)).pipe(
      map(obj => {
        const results: AgendaItem[] = [];
        for(const [key, session] of Object.entries(obj || {})) {

          if((session.time && session.label) || allowInvalid) {
            results.push({
              ...session,
              key,
              dynamicTime: session.time,
              type: 'agenda',
            });
          }
        }

        results.sort((a,b) => (a.dynamicTime || Infinity) - (b.dynamicTime || Infinity));
        return results;
      }),
    );
  }

  getScheduleConfig(regionKey: string, eventKey: string, type: 'judging'|'games'): Observable<ScheduleConfig> {
    return objectVal<ScheduleConfig>(ref(this.db, `regions/${regionKey}/events/${eventKey}/${type}/config`)).pipe(
      map(obj => {

        if(type === 'judging') {
          return {
            count: obj?.count || 4,
            duration: obj?.duration ||  1000 * 60 * 30,
            resetDuration: obj?.resetDuration ||  1000 * 60 * 10,
            locationNames: obj?.locationNames,
          }
        }

        return {
          count: obj?.count || 4,
          duration: obj?.duration ||  1000 * 60 * 2.5,
          resetDuration: obj?.resetDuration ||  1000 * 60 * 7.5,
          maxConcurrentCount: obj?.maxConcurrentCount || obj?.count || 4,
          locationNames: obj?.locationNames,
        }
      }),
    );
  }

  getAllRubricStatuses(regionKey: string, eventKey: string) {
    return combineLatest([
      this.getRubric(regionKey, eventKey),
      objectVal<{[teamNumber: string]: TeamRubric}>(ref(this.db, `regions/${regionKey}/events/${eventKey}/judging/rubrics`)),
    ]).pipe(map(([rubric, teamRubrics]) => {
      const result: {[teamNumber: string]: {status: 'in-progress'|'complete', percent: number, scores: {[sectionId: string]: number}, awards?: TeamAwards, judgeCounts?: {started: number, complete: number}}} = {};

      const teams = Object.keys(teamRubrics || {});
      for(const team of teams) {
        const teamRubric = teamRubrics[team];
        let total = 0;
        let complete = 0;

        const judgeCount = Object.keys(teamRubric.judges || {}).length;
        const scores: {[sectionId: string]: number} = {coreValues: 0};

        for(const item of rubric.feedback) {
          total += 2;
          if(teamRubric?.feedback?.[item.id]?.greatJob) {
            complete++;
          }
          if(teamRubric?.feedback?.[item.id]?.thinkAbout) {
            complete++;
          }
        }

        const judgeCounts: {[judgeUid: string]: {total: number, complete: number}} = {};

        if(!judgeCount) {
          total += 20;
        }
        for(const section of rubric.sections) {
          for(const item of section.items) {
            for(let i=0; i < item.scales.length; i++) {
              let judgeScaleCount = 0;
              let judgeScaleSum = 0;
              for(const [judgeUid, judgeSection] of Object.entries(teamRubric.judges || {})) {
                if(!judgeCounts[judgeUid]) {
                  judgeCounts[judgeUid] = {total: 0, complete: 0};
                }

                total++;
                judgeCounts[judgeUid].total++;
                const d = judgeSection?.sections?.[section.id]?.[item.id]?.[i];
                const value = d?.value;
                if(value !== null && value !== undefined) {
                  complete++;
                  judgeCounts[judgeUid].complete++;
                  judgeScaleCount++;
                  judgeScaleSum += value;
                }
                if(value === (RUBRIC_SCALE - 1)) {
                  total++;
                  if(d?.justification) {
                    complete++;
                  }
                }
              }

              if(!scores[section.id]) {
                scores[section.id] = 0;
              }

              scores[section.id] += (judgeScaleSum / judgeScaleCount) + 1;
              if(item.scales[i].coreValues) {
                scores['coreValues'] += (judgeScaleSum / judgeScaleCount) + 1;
              }
            }
          }
        }

        // Awards
        total++;
        if(teamRubric?.awards) {
          complete++;
        }

        const percent = complete / total;
        let started = 0;
        let judgeComplete = 0;

        for(const c of Object.values(judgeCounts)) {
          started++;
          if(c.complete === c.total) {
            judgeComplete++;
          }
        }

        let completedJudgeCounts = {started, complete: judgeComplete};

        if(complete) {
          const isComplete = complete === total && (completedJudgeCounts?.started === completedJudgeCounts?.complete && completedJudgeCounts?.started);
          result[team] = {percent, status: isComplete ? 'complete' : 'in-progress', scores, awards: teamRubric?.awards, judgeCounts: completedJudgeCounts};
        }
      }

      return result;
    }))
  }

  getAllScoresheetStatuses(regionKey: string, eventKey: string) {
    return objectVal<{[teamNumber: string]: TeamScoresheets}>(ref(this.db, `regions/${regionKey}/events/${eventKey}/games/scoresheets`)).pipe(map(scores => {
      const result: {[teamNumber: string]: ScoresheetStatuses} = {};

      for(const [teamNumber, matchTypes] of Object.entries(scores || {})) {
        if(!result[teamNumber]) {
          result[teamNumber] = {};
        }

        function addStatuses(matchType: 'practice'|'rounds') {
          if(!result[teamNumber][matchType]) {
            result[teamNumber][matchType] = {};
          }
          for(const [round, score] of Object.entries(matchTypes[matchType] || {})) {
            let status: 'submitted'|'in-progress'|'pending-update' = score.submitted ? 'submitted' : 'in-progress';

            if(status === 'submitted') {
              const hash = generateHash(JSON.stringify(score.items || {}));

              if(hash !== score?.submitted?.hash) {
                status = 'pending-update';
              }
            }

            result[teamNumber][matchType][round] = {status, score: score.submitted?.score};
          }
        }

        addStatuses('practice');
        addStatuses('rounds');
      }

      return result;
    }));
  }

  getLeaderboard(regionKey: string, eventKey: string): Observable<Leaderboard|null> {
    return combineLatest([this.getTeams(regionKey, eventKey), this.getSetting(regionKey, eventKey, 'leaderboard')]).pipe(
      map(([teams, leaderboardType]) => {
        if(!teams?.length) return null;

        const sortedTeams = teams.map(t => ({...t, scores: getLeaderboardScores(t.scores)}));

        for(const t of sortedTeams) {
          t.scores.sort((a, b) => b - a);
        }

        sortedTeams.sort((a, b) => {
          if(a.scores.length && !b.scores.length) {
            return -1;
          }

          if(!a.scores.length && b.scores.length) {
            return 1;
          }

          if(leaderboardType === 'average') {
            let aAvg = 0;
            for(let s of a.scores) {
              aAvg += s;
            }
            aAvg /= a.scores.length;
            let bAvg = 0; 
            for(let s of b.scores) {
              bAvg += s;
            }
            bAvg /= b.scores.length;

            return bAvg - aAvg;
          } else {
            for(let i = 0; i < Math.max(a.scores.length, b.scores.length); i++) {
              if (a.scores[i] === b.scores[i]) {
                continue;
              }
              return b.scores[i] - a.scores[i];
            }
          }

          return 0;
        });

        const results: Leaderboard = [];
        for(let i = 0; i < sortedTeams.length; i++) {
          const t = sortedTeams[i];

          const tempScores = getLeaderboardScores(teams.find(t2 => t2.teamNumber === t.teamNumber)?.scores);
          let averageScore = 0;
          if(tempScores.length) {
            for(let s of tempScores) {
              averageScore += s;
            }
            averageScore /= tempScores.length;
          }

          results.push({
            rank: t.scores.length ? (allScoresAreEqual(t.scores, sortedTeams[i - 1]?.scores) ? results[i - 1].rank : (i + 1)) : undefined,
            teamNumber: t.teamNumber,
            name: t.name,
            scores: getLeaderboardScores(teams.find(t2 => t2.teamNumber === t.teamNumber)?.scores, true),
            practiceScores: getLeaderboardScores(teams.find(t2 => t2.teamNumber === t.teamNumber)?.practiceScores, true),
            highestScore: t.scores.length ? Math.max(...t.scores) : undefined,
            averageScore: tempScores.length ? averageScore : undefined,
          });
        }
        
        return results;
      }
    ));
  }

  getLocationAssignments(regionKey: String, eventKey: String, type:'games'|'judging') {
    return objectVal<LocationAssignments>(ref(this.db, `/regions/${regionKey}/events/${eventKey}/${type}/locationAssignments`));
  }

  getLocationAssignment(regionKey: String, eventKey: String, type:'games'|'judging', uid: string) {
    return objectVal<number|null>(ref(this.db, `/regions/${regionKey}/events/${eventKey}/${type}/locationAssignments/${uid}`));
  }

  getRubric(regionKey: string, eventKey: string) {
    return this.getSetting(regionKey, eventKey, 'rubric').pipe(switchMap(rubric => objectVal<Rubric>(ref(this.db, `rubrics/${rubric === 'future' ? 'future' : 'founders'}`))));
  }

  getScoresheet(regionKey: string, eventKey: string) {
    return this.getSetting(regionKey, eventKey, 'scoresheet').pipe(switchMap(scoresheet => objectVal<Scoresheet>(ref(this.db, `scoresheets/${scoresheet === 'future' ? 'future' : 'founders'}`))));
  }

  getAwards(regionKey: string, eventKey: string) {
    return objectVal<Awards>(ref(this.db, `regions/${regionKey}/events/${eventKey}/judging/awardsConfig`));
  }

  getAwardsList(regionKey: string, eventKey: string) {
    return this.getAwards(regionKey, eventKey).pipe(map(obj => Object.values(obj || {}).sort((a,b) => a.order - b.order)));
  }
}

function allScoresAreEqual(scoresA: number[], scoresB: number[]) {
  const setA = new Set(scoresA);
  const setB = new Set(scoresB);

  if(setA.size !== setB.size) return false;

  const combinedSet = new Set([...scoresA, ...scoresB]);

  return combinedSet.size === setA.size;
}

function getLeaderboardScores(scores?: number[]|{[index: string]: number}, preserveIndexes?: boolean): number[] {
  if(!scores) return [];

  if(scores instanceof Array) {
    if(preserveIndexes) {
      return scores;
    }
    return [...scores.filter(s => s !== undefined)];
  }

  if(typeof scores === 'object') {
    const result: number[] = [];
    for(const [key, s] of Object.entries(scores)) {
      if(s !== undefined) {
        if(preserveIndexes) {
          result[+key] = s;
        } else {
          result.push(s);
        }
      }
    }

    return result;
  }

  return [];
}
